import React, { useState, useRef } from 'react';
import axiosInstance from '../../../../axiosConfig';  // Custom Axios instance
import axios from 'axios';  // Original Axios library
import styles from '../../MicroProbe/Companies/ChatNumbers.module.css';  // Import the CSS module
import SummaryDisplayChat from '../../MicroProbe/Companies/SummaryDisplay_Chat';

const SPEED_NAMES = ['Quick', 'Fast', 'Balanced', 'Detailed', 'Thorough'];

const ChatNumbers = ({ inputData }) => {
  const [customerRequest, setCustomerRequest] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [displayChatHistory, setDisplayChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [speed, setSpeed] = useState(2); // 0 = fastest, 4 = slowest
  const source = useRef(axios.CancelToken.source());
  
  // NEW: Track last request time for debouncing
  const lastRequestTime = useRef(0);
  const MIN_REQUEST_INTERVAL = 2000; // 2 seconds between requests

  const handleSubmit = (event) => {
    event.preventDefault();
    
    // NEW: Check if enough time has passed since last request
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime.current;
    
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      const waitTime = Math.ceil((MIN_REQUEST_INTERVAL - timeSinceLastRequest) / 1000);
      setResponseMessage(`Please wait ${waitTime} second${waitTime > 1 ? 's' : ''} before sending another request.`);
      return;
    }
    
    // Clear any previous error messages
    setResponseMessage('');
    setLoading(true);
    
    // NEW: Update last request time
    lastRequestTime.current = now;
    
    const maxRetries = 3; // REDUCED: Backend now has retry logic, so frontend needs fewer retries
    const delay = 2000; 
    let retries = 0;

    const fetchData = async () => {
      try {
        if (source.current) {
          source.current.cancel('Operation canceled by the user.');
        }
        source.current = axios.CancelToken.source();

        const response = await axiosInstance.post('/chat_with_numbers/', {
            customer_request: customerRequest,
            chat_history: chatHistory,
            speed: speed,
            context: {
                type: 'crypto',
                defaults: {
                    crypto_symbol: inputData.code,
                    quote_symbol: 'USD'
                }
            }
        }, {
          cancelToken: source.current.token,
        });

        if (response.data.error) {
          throw new Error(response.data.error);
        }

        // Parse response to extract text and images separately
        let responseText = response.data.response;
        const extractedImages = [];
        
        // Extract image URLs from HTML img tags in the response
        const imgRegex = /<img[^>]+src="([^">]+)"/g;
        let match;
        while ((match = imgRegex.exec(responseText)) !== null) {
          extractedImages.push(match[1]);
        }
        
        // Remove all HTML image-related tags from the text
        responseText = responseText.replace(/<br>/g, '\n');
        responseText = responseText.replace(/<div[^>]*>.*?<img[^>]*>.*?<\/div>/g, '');
        responseText = responseText.replace(/<img[^>]*>/g, '');
        responseText = responseText.trim();
        
        // Also add images from response.data.new_images if they exist
        if (response.data.new_images && response.data.new_images.length > 0) {
          extractedImages.push(...response.data.new_images);
        }

        setChatHistory([
          ...chatHistory,
          { role: 'user', content: customerRequest },
          { role: 'assistant', content: responseText },
        ]);
        setDisplayChatHistory([
          ...displayChatHistory,
          { role: 'user', content: customerRequest },
          { role: 'assistant', content: responseText, images: extractedImages },
        ]);

        setCustomerRequest('');
        setResponseMessage('');
        setLoading(false);
      } catch (error) {
        console.error('There was an error!', error);

        // NEW: Better error handling for different error types
        if (axios.isCancel(error)) {
          console.log('Request canceled:', error.message);
          setLoading(false);
          return;
        }

        // Handle 429 (rate limit) errors specifically
        if (error.response && error.response.status === 429) {
          setResponseMessage('Please wait a moment before sending another request.');
          setLoading(false);
          return;
        }

        // Retry logic for connection errors
        if (error.message === 'Connection error.' && retries < maxRetries) {
          retries += 1;
          console.log(`Connection error. Retrying (${retries}/${maxRetries}) in ${delay / 1000} seconds...`);
          setTimeout(fetchData, delay);
        } else {
          // Final error message
          const errorMsg = error.response?.data?.error || error.message || 'An error occurred. Please try again.';
          setResponseMessage(errorMsg);
          setLoading(false);
        }
      }
    };

    fetchData();
  };

  const handleChatButtonClick = () => {
    setShowChat(!showChat); 
    if (showChat) { 
      setChatHistory([]); 
      setDisplayChatHistory([]);
      setResponseMessage(''); // NEW: Clear error messages when closing chat
      lastRequestTime.current = 0; // NEW: Reset throttle when closing chat
    }
  };

  return (
    <div className={styles.wrapper}>
      <h3>Analyze</h3>
      <button onClick={handleChatButtonClick} className={styles.chatButton}>Chat with Numbers</button>
      {showChat && (
        <>
          <div className={styles.chatContainer}>
            <div className={styles.messagesArea}>
              {displayChatHistory.map((message, index) => (
                <div key={index} className={`${styles.messageWrapper} ${styles[message.role]}`}>
                  <div className={`${styles.messageBubble} ${styles[message.role]}`}>
                    {message.role === 'user' ? (
                      <pre className={styles.messageContent}>{message.content}</pre>
                    ) : (
                      <>
                        <SummaryDisplayChat content={message.content} />
                        {message.images && message.images.length > 0 && message.images.map((image, imgIdx) => (
                          <div key={imgIdx} className={styles.scrollableImageContainer}>
                            <img src={image} alt="Crypto related visual" className={styles.image} />
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              ))}

              {loading && <p className={styles.loadingText}>Analyzing your request...</p>}

              {responseMessage && (
                <div className={styles.errorMessage}>
                  {responseMessage}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className={styles.formContainer}>
              <div className={styles.formTopRow}>
                <textarea
                  value={customerRequest}
                  onChange={e => { setCustomerRequest(e.target.value); setResponseMessage(''); }}
                  placeholder="Ask about numbers..."
                  rows="1"
                  className={styles.textarea}
                />
                <div className={styles.speedSection}>
                  <span className={styles.speedSectionLabel}>
                    Thinking effort
                  </span>
                  <span className={styles.speedLabel}>Fast</span>
                  <div className={styles.speedGrids}>
                    {[0, 1, 2, 3, 4].map(i => (
                      <div
                        key={i}
                        role="button"
                        tabIndex={0}
                        className={`${styles.speedCell} ${i <= speed ? styles.speedCellActive : ''} ${i === speed ? styles.speedCellSelected : ''}`}
                        onClick={() => setSpeed(i)}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setSpeed(i)}
                        aria-label={`Speed: ${SPEED_NAMES[i]}`}
                      />
                    ))}
                  </div>
                  <span className={styles.speedLabel}>Slow</span>
                </div>
              </div>
              <div className={styles.formBottomRow}>
                <button
                  type="submit"
                  disabled={loading}
                  className={styles.submitButton}
                  title="Send"
                >
                  {loading ? '…' : '↑'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatNumbers;